import { useState } from 'react';
import { motion } from 'motion/react';
import { Button } from '../design-system/Button';
import { Image } from '../design-system';
import { SoldStatusPanel } from '../design-system/SoldStatusPanel';
import type { ApiClient, IframePoolOrder, IframePoolOrderItem } from '../api';
import { BuybackConfirmModal } from './BuybackConfirmModal';
import { RevealShippingModal } from './RevealShippingModal';
import { readSkipBuybackConfirm } from './skipBuybackConfirm';
import { computeProfit } from './ProfitDisplay';
import { FmvCaption } from './FmvCaption';
import { ProfitLine } from './ProfitLine';
import { FmvOnly } from './FmvOnly';
import { TypewriterTitle } from './TypewriterTitle';
import { RollingNumber } from './RollingNumber';
import { WinnerBadgeBackdrop } from './WinnerBadgeBackdrop';
import { RevealStage } from './RevealStage';
import {
  useRevealSequence,
  ROLL_START_DELAY_MS,
} from './useRevealSequence';
import { useDelayedFlag } from './useDelayedFlag';

export type SellState = 'idle' | 'selling' | 'sold';

export interface WinnerStepProps {
  /** API client — used by the Create Shipping modal to fetch fee figures. */
  client: ApiClient;
  item: IframePoolOrderItem;
  offer: IframePoolOrder['poolOffer'];
  sellState: SellState;
  /** Fires the buyback. The optional `afterSold` runs after the
   *  network call succeeds — used by the sell-only path to close
   *  reveal and return to the pack page. */
  onSell: (afterSold?: () => void) => void;
  onBuyAgain: () => void;
  /** "Create Shipping" link — hands off to the host's shipping flow.
   *  Also re-used as the close path back to the pack page from
   *  upstream callers. */
  onShowPack: () => void;
  /** "Confirm Shipping" on the reveal modal — start the delivery flow for
   *  THIS item (the host opens the create-shipping page seeded with it).
   *  Falls back to onShowPack when not provided. */
  onShip?: (item: IframePoolOrderItem) => void;
  /** Fired after a successful buyback of THIS pack. The reveal screen
   *  decides what's next: advance to the next unsold pack, or — once
   *  every pack in the order is sold — return to the pack page. Replaces
   *  the old "sell → onShowPack" so multi-pack orders don't bail out
   *  after the first sale. */
  onSold: () => void;
  /** When `true`, the entire winner screen paints in its FINAL state
   *  with no entrance choreography — used for returning views (the
   *  pack was already opened before this screen mounted). When `false`
   *  (a live rip), the one-time cinematic reveal plays as before. */
  instant?: boolean;
}

/**
 * Winner screen — Figma 727:8893 / 729:14222. Rendered after the
 * rotating-rays reveal hands off.
 *
 * Reveal choreography (one beat each, see useRevealSequence):
 *   1. Title types itself (typewriter, uppercase).
 *   2. Card image fades + lifts in.
 *   3. Big PROFIT line lifts in (skipped when no positive delta).
 *   4. Small "Fair Market Value $X" caption settles above PROFIT.
 *      Falls back to the standalone FMV block when there's no profit.
 *   5. Buyback CTA fades + scales in.
 *
 * Card image is sized by WIDTH (per-breakpoint cap) rather than
 * filling vertical flex-space — otherwise the slab balloons to
 * fill the iframe and dwarfs the title, breaking the Figma
 * proportions.
 */
export function WinnerStep({
  client,
  item,
  offer,
  sellState,
  onSell,
  onBuyAgain,
  onShowPack,
  onShip,
  onSold,
  instant = false,
}: WinnerStepProps) {
  const bestOffer = item.bestActiveOffer;
  const badgeName = item.badge?.name ?? null;
  // Pedestal the card stands on. Operator override from the offer, else
  // the bundled default (lives in public/, served at the site root).
  const pedestalSrc =
    offer.pedestalImageUrl ?? '/templates/modern/default-piedestal.png';
  const profitAmount = computeProfit(item, offer.price);
  const hasProfit = profitAmount !== null;

  const { titleDone, visible } = useRevealSequence({
    hasProfit,
    hasBadge: badgeName !== null,
    instant,
  });

  // Two-tact appearance for any block that hosts an odometer:
  // the row fades in showing zeroes, then ROLL_START_DELAY_MS
  // later the digits actually roll. Keeps every PROFIT / FMV /
  // Buyback beat reading as "land → roll" instead of doing both
  // at once.
  const profitRoll = useDelayedFlag(visible('profit'), ROLL_START_DELAY_MS);
  const fmvRoll = useDelayedFlag(visible('fmv'), ROLL_START_DELAY_MS);
  const ctaRoll = useDelayedFlag(visible('cta'), ROLL_START_DELAY_MS);

  // Create Shipping CTA opens this modal — reviews fees & countdown
  // before the user commits to the ship flow.
  const [showShippingModal, setShowShippingModal] = useState(false);

  return (
    <>
    <motion.div
      initial={{ opacity: instant ? 1 : 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: instant ? 0 : 0.4, ease: 'easeOut' }}
      className="relative flex min-h-full w-full flex-col items-center"
    >
      {/* Huge outline badge name in the background (e.g. "GRAIL")
          + central bloom. Only rendered when there's a badge —
          Figma 745:72070. Has its own beat AFTER the card lands —
          the bloom rises first, the letters materialise on top of
          it, then the profit/FMV/CTA cascade takes over. */}
      {badgeName && (
        <WinnerBadgeBackdrop
          badge={badgeName}
          visible={visible('badge')}
          instant={instant}
        />
      )}

      {/* Single column: title (top) -> card image (fills the middle,
          shrinks) -> bottom panel (CTAs, bottom). The column `grow`s to
          fill the viewport-height winner wrapper (its `min-h-full`
          parent creates the free space `grow` absorbs). The image is
          the only flexible element (`grow` + `maxHeight:100%`), so it
          shrinks to keep BOTH the title and the bottom CTAs on-screen
          without scrolling. Only when the title + bottom panel alone
          exceed the viewport does the wrapper's `overflow-y-auto` kick
          in (scroll fallback) - nothing gets clipped either way. */}
      <div className="flex grow basis-0 min-h-0 flex-col items-center w-full px-6 py-6 gap-10 text-center">
        {/* Wrapper exists so Design Constructor's right-click
            inspector can target the title block. TypewriterTitle
            renders an <h2> with per-letter spans; the data attr
            lives on a stable parent so the inspector doesn't
            re-hit mid-animation. */}
        <div data-co-component="WinnerStepTitle" className="w-full flex justify-center shrink-0">
          <TypewriterTitle
            text={item.title}
            onComplete={titleDone}
            instant={instant}
            className="font-bold uppercase text-center max-w-[760px] tracking-[-0.4px] leading-[1.25]"
            style={{
              color: 'var(--co-WinnerStepTitle-color, #FFFFFF)',
              fontFamily:
                'var(--co-WinnerStepTitle-font, "Inter"), Inter, sans-serif',
              fontSize: 'var(--co-WinnerStepTitle-size, 32px)',
            }}
          />
        </div>

        {/* Card image — height-clamped so it shrinks on short
            iframe heights (otherwise width-based sizing pushed the
            bottom panel below the viewport). Aspect 232/367 from
            Figma drives width once height settles. */}
        <RevealStage
          visible={visible('image')}
          instant={instant}
          className="relative grow min-h-0 w-full flex items-center justify-center"
        >
          {/* Stage = a bottom-anchored column: [card grows] above [pedestal].
              The card rests its visible base on ONE contact line regardless of
              the source image's aspect ratio (object-bottom), then sinks a few
              % into the plinth (the "seat"). The plinth is drawn in FRONT, so
              the card's hard bottom edge is tucked behind its near rim and reads
              as "standing in" the plinth, never "pasted over" it. Robust to any
              card size (a tall slab ends up narrow, a wide raw card wider — both
              on the same baseline) and to operator-custom pedestals (the seat is
              relative to the card, not the plinth's internal geometry). */}
          {/* Fixed-aspect SCENE — the whole card+plinth composition lives in one
              box with a LOCKED aspect ratio, and the card and plinth are sized in
              PERCENTAGES of it. So the entire scene scales uniformly at every
              resolution and the card-to-plinth proportion is identical everywhere
              (the old flex sizing mixed vw width with vh height, so the ratio
              drifted between portrait and landscape iframes). The box is the
              largest that still fits the iframe width, the viewport height and
              the flex middle space, always preserving its aspect. */}
          <div
            className="relative"
            style={{
              // sceneW / sceneH — drives every inner %. Tune to frame the plinth.
              aspectRatio: '320 / 500',
              // Largest scene that fits width (92vw → width = height*320/500),
              // viewport height (56vh / 540px) and the flex middle (maxHeight).
              height: 'min(56vh, 540px, 92vw * 500 / 320)',
              maxHeight: '100%',
            }}
          >
            {/* Pedestal — BEHIND the card (z-0), bottom-centred. Width is a % of
                the scene, so it always scales in lock-step with the card.
                Decorative (aria-hidden). SIZE KNOB. */}
            <img
              src={pedestalSrc}
              alt=""
              aria-hidden
              draggable={false}
              className="pointer-events-none absolute bottom-0 left-1/2 z-0 max-w-none select-none"
              style={{ width: '100%', transform: 'translateX(-50%)' }}
            />
            {/* Card — IN FRONT (z-10), top-centred, height a % of the scene;
                width follows the card aspect. object-bottom keeps any image's
                base on the contact line where it meets the plinth surface.
                Card-height % is the seat knob (taller → base sinks lower onto
                the disc). */}
            <div
              className="absolute left-1/2 top-0 z-10"
              style={{
                height: '84%',
                aspectRatio: '232 / 367',
                transform: 'translateX(-50%)',
              }}
            >
              <Image
                src={item.imageUrl}
                alt={item.title}
                className="absolute inset-0"
                imgClassName="absolute inset-0 h-full w-full object-contain object-bottom"
                width={232}
                height={367}
                quality={95}
              />
            </div>
          </div>
        </RevealStage>

        {/* Bottom panel — Buyback CTA + Create Shipping link.
              • Visible when a buyback offer is still open AND it hasn't
                been accepted yet (`sellState !== 'sold'`). The user picks
                between buyback ("Accept Offer" inside BuybackPanel) and
                "Create Shipping" — those are the only two paths.
              • If no buyback offer exists at all, only the Create
                Shipping link is shown — it's the single remaining
                action the user can take from here.
              • Once the offer has been accepted (`sold`) the whole
                panel disappears: nothing more to do. */}
        {sellState !== 'sold' && (
          <div className="w-full max-w-[480px] mx-auto flex flex-col items-stretch gap-6 shrink-0">
            {bestOffer && (
              <BuybackPanel
                offer={bestOffer}
                state={sellState}
                onSell={onSell}
                onBuyAgain={onBuyAgain}
                onSold={onSold}
                item={item}
                profitAmount={profitAmount}
                visibleProfit={visible('profit')}
                visibleFmv={visible('fmv')}
                visibleCta={visible('cta')}
                profitRoll={profitRoll}
                fmvRoll={fmvRoll}
                ctaRoll={ctaRoll}
                instant={instant}
              />
            )}

            {/* Create Shipping — visible ONLY when the item is in the
                user's possession (`status === 'on_hands'`) AND the
                buyback offer hasn't been accepted yet. Other statuses
                (`shipped`, `sold`, `pending_ship`, …) would make the
                shipping CTA misleading or impossible.

                Click opens `RevealShippingModal` — countdown +
                fees breakdown + secondary buyback fallback. The
                old version handed off straight to `onShowPack`,
                bypassing the cost review. */}
            {item.status === 'on_hands' && (
              <RevealStage
                visible={visible('cta')}
                instant={instant}
                className="self-center"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowShippingModal(true)}
                  className="mx-auto !h-auto !px-0 underline underline-offset-4 type-subhead font-normal"
                >
                  Create Shipping
                </Button>
              </RevealStage>
            )}
          </div>
        )}
      </div>
    </motion.div>

    {/* Shipping confirmation modal — opened from the Create
        Shipping ghost link above. Lives at WinnerStep level (not
        inside BuybackPanel) so the modal frame anchors to the
        whole screen, not the bottom-panel column. Gated on
        `bestOffer` because the Buyback fallback inside the
        modal needs a price to display. */}
    {showShippingModal && bestOffer && (
      <RevealShippingModal
        client={client}
        item={item}
        offer={bestOffer}
        onClose={() => setShowShippingModal(false)}
        onConfirm={() => {
          setShowShippingModal(false);
          // Confirm Shipping → start the in-app delivery flow for this item
          // (address + options + create/charge). Fall back to the plain
          // pack-page handoff when the host hasn't wired the ship flow.
          if (onShip) onShip(item);
          else onShowPack();
        }}
        onBuyback={() => {
          setShowShippingModal(false);
          // Cancel the ship & accept the buyback offer instead. Once the
          // sell lands, hand off to the reveal screen's post-sell logic
          // (advance to the next unsold pack, or close once all sold) —
          // same as the plain "Accept Offer" path.
          onSell(onSold);
        }}
      />
    )}
    </>
  );
}

function BuybackPanel({
  item,
  offer,
  state,
  onSell,
  onBuyAgain,
  onSold,
  profitAmount,
  visibleProfit,
  visibleFmv,
  visibleCta,
  profitRoll,
  fmvRoll,
  ctaRoll,
  instant,
}: {
  item: IframePoolOrderItem;
  offer: NonNullable<IframePoolOrderItem['bestActiveOffer']>;
  state: SellState;
  onSell: (afterSold?: () => void) => void;
  onBuyAgain: () => void;
  /** Post-sell callback from the reveal screen — advances to the next
   *  unsold pack, or returns to the pack page once the whole order is
   *  sold. Used by the sell-only ("Accept Offer", no Rip Again) path. */
  onSold: () => void;
  profitAmount: number | null;
  /** Container-visibility flags from the orchestrator. */
  visibleProfit: boolean;
  visibleFmv: boolean;
  visibleCta: boolean;
  /** Odometer-start flags, delayed past the row entrance so the
   *  digits don't roll while the row is still fading in. */
  profitRoll: boolean;
  fmvRoll: boolean;
  ctaRoll: boolean;
  /** Returning view — paint every row + odometer in its final state
   *  with no entrance / no roll. Forwarded down to the RollingNumbers
   *  inside FmvCaption / ProfitLine / FmvOnly and the Buyback CTA. */
  instant: boolean;
}) {
  const [showModal, setShowModal] = useState(false);

  const handleBuybackClick = () => {
    // Skip the confirmation when the user previously ticked "Don't
    // show this again" — and replay the SAME action they picked
    // last time. Storage-tier only (localStorage / sessionStorage):
    // clearing storage actually clears the flag now. The old
    // module-level in-memory tier was removed because it survived
    // HMR + LS purges, making the modal impossible to bring back
    // without a full server restart.
    const remembered = readSkipBuybackConfirm();
    if (remembered === 'acceptAndRip') {
      onSell();
      onBuyAgain();
      return;
    }
    if (remembered === 'accept') {
      // Sell-only: hand off to the reveal screen's post-sell logic once
      // the network round-trip lands, mirroring the modal-confirmed path.
      onSell(onSold);
      return;
    }
    setShowModal(true);
  };

  const handleAccept = () => {
    setShowModal(false);
    // Plain "Accept Offer" finishes the buyback, then hands off to the
    // reveal screen: advance to the next unsold pack, or return to the
    // pack page once every pack in the order is sold.
    onSell(onSold);
  };
  const handleAcceptAndRip = () => {
    setShowModal(false);
    onSell();
    // Buy another pack from the same offer right after the sell
    // request goes out. `onBuyAgain` is the host's standard
    // RIP-flow trigger; it'll mount the next reveal cycle once
    // the new order completes.
    onBuyAgain();
  };

  if (state === 'sold') {
    return (
      <SoldStatusPanel
        title={`✓ Sold for $${offer.grossPrice.toFixed(2)}`}
        subtitle="Added to your balance"
      />
    );
  }

  const hasFmv =
    item.fairMarketValue != null &&
    Number.isFinite(item.fairMarketValue) &&
    item.fairMarketValue > 0;
  const hasProfit = profitAmount !== null;

  return (
    <div className="flex flex-col items-center gap-4 w-full">
      {hasProfit ? (
        // Two-line layout: small FMV caption visually above big
        // PROFIT. DOM order = visual order. Reveal order is
        // reversed (PROFIT first, caption second) — the orchestrator
        // controls visibility per row, not the DOM. Inside each
        // row, the odometer waits an extra beat after the row
        // lands before rolling — see `*Roll` flags.
        <div className="flex flex-col items-center">
          <RevealStage visible={visibleFmv} instant={instant}>
            <FmvCaption fmv={item.fairMarketValue!} roll={fmvRoll} instant={instant} />
          </RevealStage>
          <RevealStage visible={visibleProfit} instant={instant}>
            <ProfitLine profit={profitAmount!} roll={profitRoll} instant={instant} />
          </RevealStage>
        </div>
      ) : (
        hasFmv && (
          <RevealStage visible={visibleFmv} instant={instant}>
            <FmvOnly fmv={item.fairMarketValue!} roll={fmvRoll} instant={instant} />
          </RevealStage>
        )
      )}

      <RevealStage visible={visibleCta} instant={instant} className="w-full">
        <Button
          variant="primary"
          size="xl"
          onClick={handleBuybackClick}
          disabled={state === 'selling'}
          loading={state === 'selling'}
          fullWidth
          className="h-16"
        >
          {state === 'selling' ? (
            'Selling…'
          ) : (
            <>
              Accept Buyback · $
              <RollingNumber value={offer.grossPrice} start={ctaRoll} instant={instant} />
            </>
          )}
        </Button>
      </RevealStage>

      {showModal && (
        <BuybackConfirmModal
          item={item}
          offer={offer}
          onAccept={handleAccept}
          onAcceptAndRipAgain={handleAcceptAndRip}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}